/**
 * The recurrence surface's pure half (P8-T2): what a stop would cancel, what a creation would
 * warn about, and the two request bodies.
 *
 * Three decisions are made here and nowhere else in `apps/web`:
 *
 *   - **the classification is the API's.** A schedule read and a stop preview both carry the
 *     occurrences the server classified, each with its disposition, the fields an edit touched and
 *     the entry's own revision. This module words those rows and groups them; it never decides which
 *     occurrence is eligible-forward, protected or edited, because that decision is made once, in
 *     `packages/domain/src/recurrence-stop.ts`, on both sides of the wire;
 *   - **the default set and the reviewed set are two different things.** The cancellation the
 *     surface *offers* is the server's `cancellableEntryIds`; the set it *sends* is that plus exactly
 *     the edited exceptions the reviewer ticked. The separation is what "include edited exceptions
 *     only with explicit reviewed selection" means at the surface: an edited occurrence is visible
 *     and unticked, and nothing about it is cancellable by accident;
 *   - **nothing is recomputed.** `requiresConfirmation`, the overlap list and the note are the
 *     server's; the count a sentence names is the length of the server's own list, so a warning
 *     cannot contradict the table under it.
 */

import type {
  OccurrenceDisposition,
  RecurrenceCreatePreviewBody,
  RecurrenceCreateRequest,
  RecurrenceDetailBody,
  RecurrenceOccurrenceBody,
  RecurrencePreviewBody,
  RecurrenceStopMode,
  RecurrenceStopPreviewBody,
  RecurrenceStopRequest,
  RecurrenceTemplateBody,
} from '@saldo/contracts';

import type { Mapped } from './forms';
import { formatMoney, formatMoneyMagnitude } from './money';

/** The currency literal the ledger surfaces accept, named from the contract so it cannot drift. */
type LedgerCurrency = RecurrenceTemplateBody['currency'];

/* ------------------------------------------------------------------- the schedule --- */

/** How often a schedule repeats, in words. */
const FREQUENCY_LABEL: Record<RecurrenceTemplateBody['frequency'], string> = {
  monthly: 'every month',
  weekly: 'every week',
  biweekly: 'every two weeks',
};

/** One schedule as the list renders it. */
export interface RecurrenceRowView {
  readonly id: string;
  readonly accountId: string;
  readonly description: string;
  readonly signedMinor: bigint;
  readonly currency: LedgerCurrency;
  readonly amount: string;
  /** The signed amount with its direction spelled out, so the sign is never a guess. */
  readonly direction: string;
  readonly cadence: string;
  readonly anchorDate: string;
  readonly endDate: string | null;
  readonly status: RecurrenceTemplateBody['status'];
  /** True while the schedule still generates; a stopped one is terminal and says so. */
  readonly active: boolean;
  readonly generatedThrough: string | null;
  /** What the watermark means in a sentence, including "nothing yet" rather than a blank. */
  readonly coverage: string;
  readonly revision: string;
}

export function recurrenceRowView(template: RecurrenceTemplateBody): RecurrenceRowView {
  const signedMinor = BigInt(template.signedMinor);
  // A magnitude with the direction spelled out, rather than a signed figure: an amount that is
  // always an expense reads better as "out" than as a minus, and the direction is then a word
  // instead of a colour (`money.ts`, `formatMoneyMagnitude`).
  const amount = formatMoneyMagnitude(signedMinor, template.currency);
  return {
    id: template.id,
    accountId: template.accountId,
    description: template.description,
    signedMinor,
    currency: template.currency,
    amount,
    direction: signedMinor < 0n ? `${amount} out` : `${amount} in`,
    cadence: FREQUENCY_LABEL[template.frequency],
    anchorDate: template.anchorDate,
    endDate: template.endDate,
    status: template.status,
    active: template.status === 'active',
    generatedThrough: template.generatedThrough,
    coverage:
      template.generatedThrough === null
        ? 'Nothing has been generated yet.'
        : `Generated through ${template.generatedThrough}.`,
    revision: template.revision,
  };
}

/* ---------------------------------------------------------------- the occurrences --- */

/**
 * What one disposition means for a stop, in the words the review shows.
 *
 * Every clause is a statement about *this* stop, not a general one: "will be cancelled", "is left
 * alone because it has moved", "is shown and left alone until you tick it".
 *
 * Two of them are statements about what the *default* stop would do with the occurrence, and
 * `stop-only` does neither: an `eligible-forward` occurrence is one the default stop cancels, and an
 * `edited-exception` is one only the default stop can tick. Those two are restated by
 * `STOP_ONLY_STATE` below; the rest are left alone under both modes and are worded once.
 */
const DISPOSITION_LABEL: Record<OccurrenceDisposition, string> = {
  'eligible-forward': 'Will be cancelled',
  'edited-exception': 'Edited — left alone unless you tick it',
  paid: 'Left alone: already paid',
  effective: 'Left alone: already effective',
  overdue: 'Left alone: overdue and unpaid',
  canceled: 'Left alone: already cancelled',
  deleted: 'Left alone: deleted',
};

/**
 * The two sentences that name the mode, restated for `stop-only`, where nothing is cancelled and
 * nothing can be ticked.
 *
 * `stop-only` stops the template and keeps every generated entry — `stopReview` carries
 * `cancelledByDefault: []` and `defaultCount: 0` for it — so a future occurrence is left alone by
 * *this* stop and says so, rather than reading "Will be cancelled" beside a count of zero. The same
 * mode renders no tick box (`RecurrenceWorkspace` hides it outside `stop-and-cancel`), so an edited
 * exception is not "unless you tick it" either. Every other disposition is left alone in both modes.
 */
const STOP_ONLY_STATE: Partial<Record<OccurrenceDisposition, string>> = {
  'eligible-forward': 'Left alone: this stop keeps every generated entry',
  'edited-exception': 'Edited — left alone: this stop keeps every generated entry',
};

/** The sentence one disposition carries under one stop mode. */
function dispositionState(disposition: OccurrenceDisposition, mode: RecurrenceStopMode): string {
  const label = DISPOSITION_LABEL[disposition];
  return mode === 'stop-only' ? (STOP_ONLY_STATE[disposition] ?? label) : label;
}

/** What was edited about one occurrence, in words, or `null` when nothing was. */
const EDIT_FIELD_LABEL: Record<string, string> = {
  amount: 'amount',
  description: 'description',
  category: 'category',
  account: 'account',
  dueDate: 'due date',
  transactionDate: 'transaction date',
};

export function editSummary(fields: readonly string[]): string | null {
  if (fields.length === 0) return null;
  const named = fields.map((field) => EDIT_FIELD_LABEL[field] ?? field);
  return `Changed: ${named.join(', ')}.`;
}

/** One occurrence as the review renders it. */
export interface OccurrenceRowView {
  readonly ordinal: number;
  /** The occurrence's own date: the slot the schedule generated it on. */
  readonly occurrenceDate: string;
  /**
   * The entry's **current** due date, straight off the wire — the same value the ledger holds. `null`
   * for a tombstone, which has no entry.
   */
  readonly dueDate: string | null;
  /**
   * The entry's current due date in words, and only when it has moved off the occurrence's own date:
   * `due 2027-01-15`. The row keeps both dates, because a reviewer deciding whether to cancel a
   * moved occurrence has to see the slot it was generated on *and* where the entry now stands — and
   * `null` when the two agree, so a row whose entry stands where the schedule put it says nothing.
   */
  readonly dueDateNote: string | null;
  readonly entryId: string | null;
  readonly disposition: OccurrenceDisposition;
  readonly state: string;
  readonly editedSummary: string | null;
  /** True when this row is in the default cancellation set. */
  readonly inDefaultSet: boolean;
  /** True when this row may only be cancelled by an explicit tick. */
  readonly selectable: boolean;
  readonly amount: string | null;
}

/**
 * One schedule's occurrences as the review renders them, in ordinal order.
 *
 * The mode is a parameter rather than a default because the sentence each row carries is about
 * *this* stop: the same eligible-forward occurrence is cancelled by the default stop and kept by
 * `stop-only`, and a caller that did not say which of the two it was rendering would be guessing at
 * the one thing the row exists to state. The classification itself is still the server's — the mode
 * only chooses the wording (`dispositionState`).
 *
 * An occurrence with no entry (a deleted tombstone) has no amount to show and no entry to name; it
 * is listed all the same, because a reviewer deciding whether to stop a schedule is entitled to see
 * that the ordinal exists and is spent.
 */
export function occurrenceRows(
  detail: RecurrenceDetailBody,
  mode: RecurrenceStopMode,
): OccurrenceRowView[] {
  const cancellable = new Set(detail.cancellableEntryIds);
  return [...detail.occurrences]
    .sort((left: RecurrenceOccurrenceBody, right: RecurrenceOccurrenceBody) => left.ordinal - right.ordinal)
    .map((occurrence) => ({
      ordinal: occurrence.ordinal,
      occurrenceDate: occurrence.occurrenceDate,
      dueDate: occurrence.dueDate,
      dueDateNote:
        occurrence.dueDate !== null && occurrence.dueDate !== occurrence.occurrenceDate
          ? `due ${occurrence.dueDate}`
          : null,
      entryId: occurrence.entryId,
      disposition: occurrence.disposition,
      state: dispositionState(occurrence.disposition, mode),
      editedSummary: editSummary(occurrence.editedFields),
      inDefaultSet: occurrence.entryId !== null && cancellable.has(occurrence.entryId),
      selectable: occurrence.disposition === 'edited-exception',
      amount:
        occurrence.signedMinor === null
          ? null
          : formatMoneyMagnitude(BigInt(occurrence.signedMinor), detail.template.currency),
    }));
}

/* ---------------------------------------------------------------- the stop review --- */

/**
 * The stop review: the two sets, separately, plus the rows behind them.
 *
 * `cancelledByDefault` is the server's `cancellableEntryIds` and `byExplicitSelection` is the
 * server's `editedExceptionEntryIds`. Keeping them apart is the whole point: the first is what a
 * plain "stop" does, the second is what only a tick can add, and a surface that merged them would
 * be offering to cancel an edited occurrence without ever having shown it.
 */
export interface StopReviewView {
  readonly templateId: string;
  readonly revision: string;
  readonly mode: RecurrenceStopMode;
  readonly rows: readonly OccurrenceRowView[];
  /** What the default stop cancels: the server's eligible-forward set. */
  readonly cancelledByDefault: readonly string[];
  /** What a tick can add: the server's edited exceptions. */
  readonly byExplicitSelection: readonly string[];
  /** What a tick adds *now*, given the current selection. */
  readonly selected: readonly string[];
  /** The exact set a commit would cancel, given the current selection. */
  readonly wouldCancel: readonly string[];
  readonly defaultCount: number;
  readonly selectedCount: number;
  readonly protectedCount: number;
}

/**
 * Build the review for one stop mode and one selection.
 *
 * The selection is filtered to the server's edited exceptions, so an id that is not one of them can
 * never reach the request — the same refusal the API makes (`InvalidStopSelectionError`), made here
 * first so the surface cannot offer what the commit would reject. Ordering follows the server's own
 * list, which is ordinal order, so the review and the commit name the entries the same way.
 */
export function stopReview(
  detail: RecurrenceDetailBody,
  mode: RecurrenceStopMode,
  selection: readonly string[],
): StopReviewView {
  const rows = occurrenceRows(detail, mode);
  const chosen = new Set(selection);
  // `stop-only` cancels nothing and names nothing: a selection alongside it is the contradiction the
  // API refuses (`InvalidStopSelectionError`), so the review offers nothing to select either.
  const stopOnly = mode === 'stop-only';
  const cancelledByDefault = stopOnly ? [] : [...detail.cancellableEntryIds];
  const byExplicitSelection = stopOnly ? [] : [...detail.editedExceptionEntryIds];
  const selected = byExplicitSelection.filter((entryId) => chosen.has(entryId));
  const wouldCancel = [...cancelledByDefault, ...selected];
  return {
    templateId: detail.template.id,
    revision: detail.template.revision,
    mode,
    rows,
    cancelledByDefault,
    byExplicitSelection,
    selected,
    wouldCancel,
    defaultCount: cancelledByDefault.length,
    selectedCount: selected.length,
    protectedCount: rows.filter((row) => !row.inDefaultSet && !row.selectable).length,
  };
}

/* ----------------------------------------------------------------- the previews --- */

/** A stored stop preview, as the surface reviews it before committing. */
export interface StopPreviewView {
  readonly id: string;
  readonly templateId: string;
  readonly revision: string;
  readonly businessToday: string;
  readonly expiresAt: string;
  readonly mode: RecurrenceStopMode;
  readonly willCancel: readonly string[];
  readonly selected: readonly string[];
  readonly willCancelCount: number;
  readonly protectedCount: number;
  readonly summary: string;
}

/**
 * What a stored stop preview will do, in words.
 *
 * The counts are the preview's own lists — never a recomputation — and the sentence says what will
 * happen to the schedule as well as to the entries, because "the template stops" is the half a
 * reader of a cancellation count would otherwise have to infer.
 */
export function stopPreviewView(preview: RecurrenceStopPreviewBody): StopPreviewView {
  const entries =
    preview.cancelledEntryIds.length === 0
      ? 'No generated entry is cancelled'
      : `${preview.cancelledEntryIds.length} generated ${preview.cancelledEntryIds.length === 1 ? 'entry is' : 'entries are'} cancelled`;
  const selected =
    preview.selectedEntryIds.length === 0
      ? ''
      : `, ${preview.selectedEntryIds.length} of them because you ticked ${preview.selectedEntryIds.length === 1 ? 'it' : 'them'}`;
  const protectedCount = preview.excluded.length;
  const kept =
    protectedCount === 0
      ? ''
      : ` ${protectedCount} ${protectedCount === 1 ? 'occurrence is' : 'occurrences are'} left alone.`;
  return {
    id: preview.id,
    templateId: preview.templateId,
    revision: preview.revision,
    businessToday: preview.businessToday,
    expiresAt: preview.expiresAt,
    mode: preview.mode,
    willCancel: [...preview.cancelledEntryIds],
    selected: [...preview.selectedEntryIds],
    willCancelCount: preview.cancelledEntryIds.length,
    protectedCount,
    summary: `${entries}${selected}. The schedule stops and never generates again.${kept}`,
  };
}

/** A stored creation preview, as the surface reviews it before committing. */
export interface CreatePreviewView {
  readonly id: string;
  readonly accountId: string;
  readonly currency: LedgerCurrency;
  readonly businessToday: string;
  readonly expiresAt: string;
  readonly occurrences: readonly { readonly ordinal: number; readonly occurrenceDate: string; readonly amount: string }[];
  readonly firstDate: string | null;
  readonly lastDate: string | null;
  /** The retained-date overlap the API found: reviewed, never silently merged. */
  readonly overlaps: readonly string[];
  readonly hasOverlap: boolean;
  readonly requiresConfirmation: boolean;
  readonly note: string;
}

/**
 * What a stored creation preview will write, and what it warns about.
 *
 * `overlaps` is built from the API's own list, and each line names the day and the entry already on
 * it. The note is the API's sentence verbatim: a warning a surface re-worded could say less than
 * the one the commit is gated on.
 */
export function createPreviewView(preview: RecurrenceCreatePreviewBody): CreatePreviewView {
  const occurrences = [...preview.occurrences]
    .sort((left, right) => left.ordinal - right.ordinal)
    .map((occurrence) => ({
      ordinal: occurrence.ordinal,
      occurrenceDate: occurrence.occurrenceDate,
      amount: formatMoneyMagnitude(BigInt(occurrence.signedMinor), occurrence.currency),
    }));
  // The retained entry keeps its **signed** figure: the point of the warning is that a day already
  // carries money, and whether that money is in or out is part of reading it.
  const overlaps = preview.overlap.map(
    (warning) =>
      `${warning.occurrenceDate}: ${warning.description} (${formatMoney(BigInt(warning.signedMinor), warning.currency)})${warning.generated ? ' — already generated by a schedule' : ''}`,
  );
  return {
    id: preview.id,
    accountId: preview.accountId,
    currency: preview.currency,
    businessToday: preview.businessToday,
    expiresAt: preview.expiresAt,
    occurrences,
    firstDate: occurrences[0]?.occurrenceDate ?? null,
    lastDate: occurrences[occurrences.length - 1]?.occurrenceDate ?? null,
    overlaps,
    hasOverlap: preview.requiresConfirmation,
    requiresConfirmation: preview.requiresConfirmation,
    note: preview.note,
  };
}

/** The overlaps of a stored create preview, which is what the acknowledgement is about. */
export function isCreatePreview(preview: RecurrencePreviewBody): preview is RecurrenceCreatePreviewBody {
  return preview.action === 'recurrence.create';
}

export function isStopPreview(preview: RecurrencePreviewBody): preview is RecurrenceStopPreviewBody {
  return preview.action === 'recurrence.stop';
}

/* ----------------------------------------------------------------- the requests --- */

/** The fields a creation needs, as the editor holds them. */
export interface RecurrenceForm {
  readonly accountId: string;
  readonly categoryId: string | null;
  readonly description: string;
  readonly amount: string;
  readonly kind: 'income' | 'expense';
  readonly frequency: RecurrenceTemplateBody['frequency'];
  readonly anchorDate: string;
  readonly endDate: string;
}

/**
 * `recurrence.create`: the schedule to write, or the reason this form cannot be sent.
 *
 * The amount is turned into a signed minor integer here, and a form with no amount or no account is
 * refused before the request rather than after it. Nothing else is validated here: the anchor, the
 * end and the frequency are the API's to refuse, and it refuses them with the same 422 whether the
 * surface guessed or not.
 */
export function toRecurrenceCreate(form: RecurrenceForm): Mapped<RecurrenceCreateRequest> {
  if (form.accountId.trim() === '') return { ok: false, error: 'Choose the account this schedule writes to.' };
  if (form.description.trim() === '') return { ok: false, error: 'Give the schedule a description.' };
  const amount = Number.parseFloat(form.amount.replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(amount) || amount === 0) {
    return { ok: false, error: 'Give the schedule an amount other than zero.' };
  }
  const minor = BigInt(Math.round(Math.abs(amount) * 100));
  const signed = form.kind === 'expense' ? -minor : minor;
  return {
    ok: true,
    body: {
      action: 'recurrence.create',
      accountId: form.accountId,
      categoryId: form.categoryId,
      description: form.description.trim(),
      signedMinor: signed.toString(),
      frequency: form.frequency,
      anchorDate: form.anchorDate,
      endDate: form.endDate.trim() === '' ? null : form.endDate,
    },
  };
}

/**
 * `recurrence.stop`: the mode and the edited exceptions a reviewer named.
 *
 * `stop-only` carries no selection at all — a selection alongside it is a contradiction the API
 * refuses (`InvalidStopSelectionError`), so the surface does not build one.
 */
export function toRecurrenceStop(
  templateId: string,
  mode: RecurrenceStopMode,
  selection: readonly string[],
): Mapped<RecurrenceStopRequest> {
  if (templateId.trim() === '') return { ok: false, error: 'No schedule to stop.' };
  const body: RecurrenceStopRequest =
    mode === 'stop-only'
      ? { action: 'recurrence.stop', templateId, mode }
      : {
          action: 'recurrence.stop',
          templateId,
          mode,
          selectedEntryIds: [...selection],
        };
  return { ok: true, body };
}
